import { Component, forwardRef, KeyValueDiffers, OnChanges, OnInit, ChangeDetectorRef, OnDestroy, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';

import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';

import { BlockComponent } from '../../../PageBuilder/components/Block/block.component';
import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { DataPointModel } from '../../../Charts/models/data-point.model';
import { DashboardChartBlockComponent } from '../../../Dashboard/components/DashboardChartBlock/dashboard-chart-block.component';
import { FixedChartSourceDataService } from '../../../Dashboard/services/fixed-chart-source-data.service';

import { ReportsSeriesPreviewService } from '../../services/report-series-preview.service';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { ClickOutsideService } from '../../../PageBuilder/services/click-outside.service';
import { ReportsChartDetailService } from '../../services/reports-chart-detail.service';
import { ChartDisplayElementModel } from '../../../Reports/models/chart-display-element.model';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { ReportsChartSeriesConverterService } from '../../../Reports/services/reports-chart-series-converter.service';
import { ReportsSeriesPreviewNewService } from '../../../../Common/Reports/services/reports-series-preview-new.service';

const reportBlockTypeEnumToRouteMap = new Map<string, string>([
    [ReportBlockTypeEnum.RECENT_TARGETING_ACTIVITY, 'Discover/targetingsearches/search'],
    [ReportBlockTypeEnum.RECENT_DISTRIBUTION_ACTIVITY, 'Discover/distributions' ],
    [ReportBlockTypeEnum.RECENT_REPORTS_DISTRIBUTIONS, 'Discover/distributions' ],
    [ReportBlockTypeEnum.RECENT_SURGE_ALERTS, 'Monitoring/alerts'],
]);

enum ListCardTypeEnum {
    DETAIL_CARD = 'DETAIL_CARD',
    STACKED_BAR_CARD = 'STACKED_BAR_CARD',
}

const reportBlockTypeEnumToListCardTypeMap = new Map<string, ListCardTypeEnum>([
    [ReportBlockTypeEnum.RECENT_TARGETING_ACTIVITY, ListCardTypeEnum.DETAIL_CARD],
    [ReportBlockTypeEnum.RECENT_DISTRIBUTION_ACTIVITY, ListCardTypeEnum.DETAIL_CARD ],
    [ReportBlockTypeEnum.RECENT_REPORTS_DISTRIBUTIONS, ListCardTypeEnum.STACKED_BAR_CARD ],
    [ReportBlockTypeEnum.RECENT_SURGE_ALERTS, ListCardTypeEnum.DETAIL_CARD],
]);

const reportBlockTypeEnumToActionMap = new Map<string, string>([
    [ReportBlockTypeEnum.RECENT_DISTRIBUTION_ACTIVITY, 'Dashboard.chart.distribution.actionButtonLabel' ],
    [ReportBlockTypeEnum.RECENT_REPORTS_DISTRIBUTIONS, 'Dashboard.chart.distribution.actionButtonLabel' ],
]);

const reportBlockTypeEnumToIconMap = new Map<string, string>([
    [ReportBlockTypeEnum.RECENT_TARGETING_ACTIVITY, 'fa-kit agpr-i-line-targeting'],
    [ReportBlockTypeEnum.RECENT_DISTRIBUTION_ACTIVITY, 'fa-kit fa-agpr-distribution' ],
    [ReportBlockTypeEnum.RECENT_REPORTS_DISTRIBUTIONS, 'fa-kit fa-agpr-distribution' ],
    [ReportBlockTypeEnum.RECENT_SURGE_ALERTS, 'fa-kit fa-agpr-momentum'],
]);


@Component({
    selector: 'agpr-list-chart-block',
    templateUrl: './list-chart-block.component.html',
    styleUrls: ['./list-chart-block.component.scss'],
    providers: [
        {
            provide: BlockComponent,
            useExisting: forwardRef(() => ListChartBlockComponent)
        }
    ],
    standalone: false
})
export class ListChartBlockComponent extends DashboardChartBlockComponent implements OnChanges, OnInit, OnDestroy {
    data: DataPointModel[];
    listCardType: ListCardTypeEnum;
    listCardTypesEnum = ListCardTypeEnum;
    mainRequest: Subscription;
    actionButtonLabel: string;
    displayTitle: boolean;
    titleIcon: string;

    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public localizationService: LocalizationService,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public coreDialogService: CoreDialogService,
        protected fixedChartSourceDataService: FixedChartSourceDataService,
        protected userPreferencesService: UserPreferencesService,
        public router: Router,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public elementRef: ElementRef,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService
    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            null,
            localizationService,
            null,
            changeDetectorRef,
            userStateService,
            null,
            null,
            coreDialogService,
            userPreferencesService,
            router,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            elementRef,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.listCardType = reportBlockTypeEnumToListCardTypeMap.get(this.block.type.key);
        this.actionButtonLabel = reportBlockTypeEnumToActionMap.get(this.block.type.key);
        const displayElement = this.block.displayElement as ChartDisplayElementModel;
        this.displayTitle = displayElement.chartHeaderSettings.title;
        this.titleIcon = reportBlockTypeEnumToIconMap.get(this.block.type.key);
    }

    ngOnChanges(): void {
        this.updateChart();
    }

    updateChart() {
        super.updateChart();
        const displayElement = this.block.displayElement as ChartDisplayElementModel;
        this.displayTitle = displayElement.chartHeaderSettings.title;
        if (this.preview) {
            this.data = this.reportSeriesPreviewService.getListChartPreviewData(this.block);
        } else {
            if (this.mainRequest) {
                this.mainRequest.unsubscribe();
            }
            this.mainRequest = this.fixedChartSourceDataService.getListChartData(this.block).subscribe(data => this.data = data);
        }
    }

    navigateTo(): void {
        this.router.navigate([reportBlockTypeEnumToRouteMap.get(this.block.type.key)]);
    }

    ngOnDestroy(): void {
        if (this.mainRequest) {
            this.mainRequest.unsubscribe();
        }
    }
}
